/**
 * 🎭 EmotionByHourChart — Feature Premium (Emotion Analytics)
 *
 * Barras empilhadas por hora com a composição de emoções (feliz, neutro,
 * triste, raiva e demais), permitindo ver a "média por hora" de cada
 * comportamento ao longo do dia do período selecionado.
 *
 * Só é renderizado no dashboard quando a organização tem a feature
 * habilitada. Sem flag → nada muda no sistema.
 *
 * Zero `any`.
 */

import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TooltipContentProps } from 'recharts';
import { EmotionStats } from '../../domain/entities/Analytics';

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface EmotionByHourChartProps {
  data: EmotionStats;
}

interface HourChartPoint {
  hourLabel: string;
  Feliz: number;
  Neutro: number;
  Triste: number;
  Raiva: number;
  Outros: number;
  total: number;
}

// ─── Constantes ────────────────────────────────────────────────────────────────

const COLOR_HAPPY   = '#22c55e';
const COLOR_NEUTRAL = '#94a3b8';
const COLOR_SAD     = '#3b82f6';
const COLOR_ANGRY   = '#ef4444';
const COLOR_OTHER   = '#eab308';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const HourTooltip = (
  { active, payload, label }: TooltipContentProps<number, string>,
): React.ReactElement | null => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as HourChartPoint;
  return (
    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-sm">
      <p className="font-semibold text-gray-900 dark:text-white mb-2">
        {label} — {point.total} pessoas
      </p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name}: {Number(p.value).toLocaleString('pt-BR')} pessoas
        </p>
      ))}
    </div>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────────

export const EmotionByHourChart: React.FC<EmotionByHourChartProps> = ({ data }) => {
  const chartData: HourChartPoint[] = data.by_hour.map((h) => ({
    hourLabel: `${String(h.hour).padStart(2, '0')}h`,
    Feliz: h.happy,
    Neutro: h.neutral,
    Triste: h.sad,
    Raiva: h.angry,
    Outros: h.surprise + h.fear + h.disgust + h.contempt,
    total: h.total,
  }));

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-1 text-gray-800 dark:text-gray-100">
        Emoções por Hora
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Composição do humor dos visitantes em cada hora (média por hora do período selecionado)
      </p>

      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-400 dark:text-gray-500 text-sm">
          Sem dados de emoções por hora no período selecionado
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} barCategoryGap="25%" barGap={1}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.4} />
            <XAxis dataKey="hourLabel" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              width={50}
              tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
            />
            <Tooltip<number, string> content={HourTooltip} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, color: '#9ca3af' }}
            />
            <Bar dataKey="Feliz"  stackId="a" fill={COLOR_HAPPY}   radius={[0, 0, 0, 0]} />
            <Bar dataKey="Neutro" stackId="a" fill={COLOR_NEUTRAL} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Triste" stackId="a" fill={COLOR_SAD}     radius={[0, 0, 0, 0]} />
            <Bar dataKey="Raiva"  stackId="a" fill={COLOR_ANGRY}   radius={[0, 0, 0, 0]} />
            <Bar dataKey="Outros" stackId="a" fill={COLOR_OTHER}   radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
